import React, { useEffect, useState } from 'react';
import { vipAPI } from '../../../services/api';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { cn } from '../../../lib/utils';
import { apiError, followUpHint, MAX_NOTE_LENGTH, todayISO } from './trackerView';
import { addDays, FOLLOW_UP_PRESETS } from './queueView';

export const emptyContactForm = () => ({ channelId: '', contactedAt: todayISO(), nextContactAt: '', note: '' });

// useContactChannels loads the active channels while active; with a single channel the form
// preselects it through onOnlyChannel.
export function useContactChannels(active, onOnlyChannel) {
  const [channels, setChannels] = useState([]);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    setError('');
    vipAPI.getContactChannels()
      .then((res) => {
        if (cancelled) return;
        const list = res.data.channels || [];
        setChannels(list);
        if (list.length === 1) onOnlyChannel?.(String(list[0].channel_id));
      })
      .catch((err) => { if (!cancelled) setError(apiError(err, 'Failed to load channels.')); });
    return () => { cancelled = true; };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
  return { channels, error };
}

function FieldLabel({ htmlFor, children, note }) {
  const Tag = htmlFor ? 'label' : 'span';
  return (
    <div className="flex items-baseline justify-between">
      <Tag htmlFor={htmlFor} className="text-[13px] font-bold text-ink">{children}</Tag>
      {note && <span className="text-xs font-medium text-ink-mute">{note}</span>}
    </div>
  );
}

function Chip({ active, onClick, children }) {
  return (
    <button
      type="button" aria-pressed={active} onClick={onClick}
      className={cn('h-11 whitespace-nowrap rounded-lg border px-3.5 text-sm font-bold transition-colors',
        active ? 'border-cta bg-cta/15 text-ink' : 'border-white/10 bg-surface text-ink-dim hover:border-white/20 hover:text-ink')}
    >
      {children}
    </button>
  );
}

// ContactedPicker: "Today" or a picked (past) day.
function ContactedPicker({ value, onChange }) {
  const today = todayISO();
  const [picking, setPicking] = useState(value !== today);
  useEffect(() => { if (value !== today) setPicking(true); }, [value, today]);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Chip active={!picking} onClick={() => { setPicking(false); onChange(today); }}>Today</Chip>
      <Chip active={picking} onClick={() => setPicking(true)}>Pick date</Chip>
      {picking && (
        <Input
          type="date" aria-label="Contact date" value={value} max={today}
          className="h-11 w-auto [color-scheme:dark]" onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

// FollowUpPicker: presets from today, or a picked day (today allowed). Clicking the selected
// option again clears the follow-up.
function FollowUpPicker({ value, onChange }) {
  const today = todayISO();
  const presetOf = (ymd) => FOLLOW_UP_PRESETS.find((p) => addDays(today, p.days) === ymd);
  const [picking, setPicking] = useState(Boolean(value) && !presetOf(value));
  useEffect(() => { if (value && !presetOf(value)) setPicking(true); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {FOLLOW_UP_PRESETS.map((p) => {
        const on = !picking && value === addDays(today, p.days);
        return (
          <Chip key={p.days} active={on} onClick={() => { setPicking(false); onChange(on ? '' : addDays(today, p.days)); }}>
            {p.label}
          </Chip>
        );
      })}
      <Chip active={picking} onClick={() => { if (picking) onChange(''); setPicking(!picking); }}>Pick a date</Chip>
      {picking && (
        <Input
          type="date" aria-label="Next contact date" value={value} min={today}
          className="h-11 w-auto [color-scheme:dark]" onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

// ContactFormFields: channel, contact day, what happened and an optional next contact; shared by
// the queue's log panel, the per-player dialog and the batch log. who names the player in the
// follow-up hint; the batch log passes its own wording (noteNote, notePlaceholder, followText).
export default function ContactFormFields({
  form, onChange, channels, who = 'the player', onSubmitShortcut,
  noteNote = 'Required', notePlaceholder = 'Who you spoke to, what you offered, how they reacted.',
  noteClassName = 'min-h-[120px]', followText,
}) {
  const set = (key) => (value) => onChange({ ...form, [key]: value });
  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      onSubmitShortcut?.();
    }
  };
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap gap-6">
        <div role="group" aria-label="Channel" className="flex min-w-0 flex-[1_1_320px] flex-col gap-2.5">
          <FieldLabel note="Required">Channel</FieldLabel>
          <div className="flex flex-wrap gap-1.5">
            {channels.map((c) => {
              const id = String(c.channel_id);
              return <Chip key={id} active={form.channelId === id} onClick={() => set('channelId')(id)}>{c.channel}</Chip>;
            })}
          </div>
        </div>
        <div role="group" aria-label="Contacted" className="flex flex-col gap-2.5">
          <FieldLabel>Contacted</FieldLabel>
          <ContactedPicker value={form.contactedAt} onChange={set('contactedAt')} />
        </div>
      </div>

      <div className="flex flex-col gap-2.5">
        <FieldLabel htmlFor="lc-note" note={noteNote}>What happened</FieldLabel>
        <Textarea
          id="lc-note" value={form.note} maxLength={MAX_NOTE_LENGTH} onKeyDown={onKeyDown}
          placeholder={notePlaceholder}
          className={cn('resize-y text-[15px] leading-relaxed', noteClassName)}
          onChange={(e) => set('note')(e.target.value)}
        />
      </div>

      <div role="group" aria-label="Next contact" className="flex flex-col gap-2.5">
        <FieldLabel note="Optional">Next contact</FieldLabel>
        <FollowUpPicker value={form.nextContactAt} onChange={set('nextContactAt')} />
        <span className="text-[13px] text-ink-dim">{followText ?? followUpHint(form.nextContactAt, who)}</span>
      </div>
    </div>
  );
}
